// Buffer local (write-ahead) de las ediciones del Modo Turno.
//
// PROBLEMA QUE RESUELVE (incidente cbozan 06-ago-2026, y sus repeticiones):
// el texto clínico que el médico escribe vive SOLO en el estado del componente
// hasta que ocurre un `blur`. Si en el medio la app se recarga, el PWA del iPad
// se mata por background, se cae la red o expira la sesión, ese texto se pierde
// sin dejar rastro — ni en el servidor ni en ningún lado. Para el médico eso es
// "perdí el turno de hoy".
//
// La estrategia es write-ahead: ANTES de mandar el PATCH dejamos el valor en
// localStorage. Si el PATCH confirma, borramos la entrada. Todo lo que queda en
// el buffer es, por definición, una edición que NUNCA se confirmó — y por lo
// tanto es candidata a recuperación en el próximo arranque.
//
// Se guarda por (tab, row, column) y no por tiempo: la última intención del
// usuario sobre una celda es la única que interesa.
//
// ⚠️ PHI cruzada entre médicos (3ª pasada del revisor, #1255). El buffer vivía
// SIN dueño: dos médicos en el MISMO computador (el escenario del mesón que
// justifica que el bloc tenga backend) con una pestaña del MISMO título (misma
// fecha, unidades distintas incluido) terminaban compartiendo el write-ahead
// del otro. `logout()` ya purga el diario del dictado (`dictado-buffer.js`,
// mismo riesgo, Ley 21.719) pero nunca tocaba esto. Dos capas, ninguna sola:
//   1. Cada clave lleva el `authToken` de sesión (síncrono, no depende de que
//      `currentUser` ya haya resuelto su GET) — un borrador escrito por A es
//      directamente ILEGIBLE con el token de B, sin esperar a que nadie lo
//      purgue. `listDrafts` filtra por el owner ACTUAL.
//   2. `purgarDrafts()` (llamado desde `logout()`, al lado de `purgarDictado()`)
//      borra el buffer entero al cerrar sesión: defensa en profundidad si el
//      token se reutiliza o si algo lee localStorage entre sesiones.
// El mismo médico, misma cuenta, recarga de página (R15) sigue funcionando
// igual: un F5 no toca el `authToken`.
//
// ⚠️ CORRECCIÓN (4ª pasada del revisor, 14-sep-2026): esta misma nota decía
// que el `authToken` "no cambia con un F5, solo con un logout/login". Es
// FALSO, y era la premisa sobre la que se apoyaba todo el diseño de arriba.
// El token también ROTA sin logout ni recarga: "Cambiar clave" y "Cerrar las
// otras sesiones" (navbar) llaman a `establecerSesion(token, {mismoUsuario:
// true})` con un token nuevo emitido por el backend, y lo mismo pasa si un
// admin revoca la sesión. Sin más, el aislamiento por dueño trataba a un
// médico como un desconocido de sí mismo: su propio borrador sin confirmar
// quedaba huérfano bajo la clave vieja, invisible para él (probes P2a/P4).
// Por eso `session-token.js` llama a `rekeyDrafts(tokenViejo, tokenNuevo)`
// en ese mismo `mismoUsuario: true` —significa literalmente "es la misma
// persona"— y no reabre el cruce: solo mueve las claves DEL dueño viejo,
// nunca las de otro médico.
//
// ⚠️ Además (misma pasada): las claves de 3 partes que escribía la versión
// desplegada hasta el 2026-09-10 (`turno.draft.v1:<tab>:<row>:<col>`, SIN
// dueño) dejaron de parsear contra el formato de 4 partes de arriba, y
// `allEntries`/`evict` las saltean por completo: no se recuperan (adoptarlas
// reabriría el cruce que este mismo fix cerró) pero tampoco vencen nunca a
// las 24 h, quedando su PHI inmortal en el disco del médico. `purgarClavesViejas()`
// las borra al cargar este módulo (arranque de la app), ver más abajo.

import { esFilaEliminada } from "./turno-row-identity";

const PREFIX = "turno.draft.v1:";
// #1449 H-E: donde se ARCHIVA un borrador huérfano (de una fila que ya no está
// en su número) cuando otro paciente escribe en la misma clave (título, fila,
// columna). El número de fila negativo no choca con ninguna fila real (arrancan
// en 2) ni con el centinela del bloc (`NOTAS_TURNO_ROW = -1`): la recuperación
// no lo encuentra en el servidor y lo ofrece como "fila eliminada".
const ARCHIVO_OFFSET = 1000000;
// Más viejo que esto se descarta al leer: un borrador de hace días casi seguro
// ya fue reescrito por otra sesión y restaurarlo sería peor que perderlo.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Techo defensivo del buffer (localStorage suele ser ~5 MB por origen). Una
// evolución larga ronda los 30-100 KB, así que esto deja lugar de sobra para un
// turno completo sin arriesgar un QuotaExceededError que rompa el guardado.
const MAX_TOTAL_CHARS = 3_000_000;

const storage = () => {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    // Safari en modo privado puede tirar al tocar localStorage.
    return null;
  }
};

// Dueño de la clave. Deliberadamente el `authToken` crudo (no el email de
// `currentUser`, que llega async por `/api/user/me/` y todavía puede no estar
// resuelto cuando el médico ya está escribiendo): así el aislamiento es
// inmediato desde el primer keystroke, sin ventana de carrera. Sin sesión
// (nunca debería pasar en /turno, que está detrás de `RequireAuth`) cae a un
// valor fijo que ningún token real puede pisar por accidente.
const ownerToken = () => {
  try {
    return localStorage.getItem("authToken") || "sin-sesion";
  } catch {
    return "sin-sesion";
  }
};

const makeKey = (tab, row, column) =>
  `${PREFIX}${encodeURIComponent(ownerToken())}:${encodeURIComponent(
    String(tab),
  )}:${row}:${encodeURIComponent(String(column))}`;

const parseKey = (key) => {
  if (!key || !key.startsWith(PREFIX)) return null;
  const parts = key.slice(PREFIX.length).split(":");
  if (parts.length !== 4) return null;
  const row = Number(parts[2]);
  if (!Number.isFinite(row)) return null;
  try {
    return {
      owner: decodeURIComponent(parts[0]),
      tab: decodeURIComponent(parts[1]),
      row,
      column: decodeURIComponent(parts[3]),
    };
  } catch {
    return null;
  }
};

const allEntries = (ls) => {
  const out = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    const parsed = parseKey(key);
    if (!parsed) continue;
    let payload = null;
    try {
      payload = JSON.parse(ls.getItem(key) || "null");
    } catch {
      payload = null;
    }
    if (!payload || typeof payload.value !== "string") {
      out.push({ key, corrupt: true, ...parsed });
      continue;
    }
    out.push({
      key,
      ...parsed,
      value: payload.value,
      ts: Number(payload.ts) || 0,
      // Metadata opcional (hoy solo la usa el centinela del bloc: `tabId` de
      // la pestaña exacta donde se escribió, para no reponerlo en OTRA
      // pestaña con el mismo título — ver `stageDraft`).
      meta: payload.meta && typeof payload.meta === "object" ? payload.meta : null,
    });
  }
  return out;
};

// Deja lugar tirando lo más viejo (y lo corrupto/vencido) hasta bajar del techo.
const evict = (ls) => {
  const entries = allEntries(ls);
  const now = Date.now();
  let total = 0;
  const live = [];
  entries.forEach((e) => {
    if (e.corrupt || !e.ts || now - e.ts > MAX_AGE_MS) {
      try {
        ls.removeItem(e.key);
      } catch {
        /* nada que hacer */
      }
      return;
    }
    total += e.value.length;
    live.push(e);
  });
  if (total <= MAX_TOTAL_CHARS) return;
  live.sort((a, b) => a.ts - b.ts); // más viejo primero
  for (const e of live) {
    if (total <= MAX_TOTAL_CHARS) break;
    try {
      ls.removeItem(e.key);
    } catch {
      /* nada que hacer */
    }
    total -= e.value.length;
  }
};

/**
 * Deja el valor en el buffer ANTES de intentar persistirlo en el servidor.
 * Nunca lanza: si el respaldo local falla, el guardado normal debe seguir.
 *
 * `meta` es opcional y se guarda tal cual junto al valor — hoy solo lo usa el
 * centinela del bloc de notas del turno, que manda `{ tabId }` para poder
 * negarse a reponerse en OTRA pestaña con el mismo título (dos unidades del
 * mismo médico, mismo día: ver `recoverUnsavedDrafts` en Turno.js).
 */
export const stageDraft = (tab, row, column, value, meta = null) => {
  const ls = storage();
  if (!ls || !tab || !row || !column) return;
  const key = makeKey(tab, row, column);
  // #1449 H-E: el huérfano de X (marcado `rowGone`) que llega a una clave
  // donde vive un borrador SIN confirmar de otro paciente (Y heredó el número)
  // va directo al archivo: desplazar el de Y lo haría pasar por "fila
  // eliminada" cuando Y sigue ahí.
  if (huerfanoContraBorradorVivoAjeno(ls, key, meta)) {
    archivarHuerfano(
      ls,
      key,
      { value: String(value ?? ""), ts: Date.now(), meta },
      meta.rowId,
    );
    return;
  }
  const nextMeta = conservarRowGone(ls, key, meta, value);
  const payload = JSON.stringify({
    value: String(value ?? ""),
    ts: Date.now(),
    ...(nextMeta && typeof nextMeta === "object" ? { meta: nextMeta } : {}),
  });
  try {
    ls.setItem(key, payload);
  } catch {
    // Casi siempre QuotaExceededError → hacemos lugar y reintentamos una vez.
    try {
      evict(ls);
      ls.setItem(key, payload);
    } catch {
      /* sin respaldo local; el PATCH sigue su curso igual */
    }
  }
};

/**
 * #1449, borde de H8: la marca `rowGone` es PEGAJOSA. `saveCell` (Turno.js) la
 * pone cuando el PATCH de esa celda recibe el 404 `row_not_found`. Pero
 * después `handleCellChange` (cada tecla) y el write-ahead de `saveCell`
 * vuelven a guardar el MISMO borrador con `{ tabId }` y sin la marca: si la
 * pisaban, lo que el médico siguió escribiendo en la fila borrada quedaba otra
 * vez sin marca. Y si la página se moría antes del próximo guardado, se
 * reponía solo en el paciente que heredó el número de fila.
 *
 * Por eso, si la clave ya tiene un borrador vigente con `rowGone`, de la misma
 * pestaña (mismo `tabId`, o sin `tabId` en alguno de los dos), el borrador
 * nuevo la conserva. La marca solo la saca `clearDraft`: el POST del extra
 * confirmado ("Usar lo mío"), descartar el conflicto, o un PATCH que el
 * servidor confirmó. No cruza a otra pestaña con el mismo título (otro
 * `tabId`) ni revive un borrador vencido.
 */
const conservarRowGone = (ls, key, meta, value) => {
  let prev = null;
  try {
    prev = JSON.parse(ls.getItem(key) || "null");
  } catch {
    return meta;
  }
  const prevMeta = prev && typeof prev.meta === "object" ? prev.meta : null;
  const vigente = Boolean(prev && prev.ts && Date.now() - Number(prev.ts) <= MAX_AGE_MS);
  const tabIdNuevo = meta && typeof meta === "object" ? meta.tabId ?? null : null;
  const tabIdPrevio = prevMeta?.tabId ?? null;
  const mismaPestana =
    tabIdNuevo == null || tabIdPrevio == null || String(tabIdNuevo) === String(tabIdPrevio);
  const textoPrevio = prev && typeof prev.value === "string" ? prev.value : "";
  // #1449 H-E: la clave es (título, fila, columna) y el número de fila se
  // REUSA. Si el borrador que está ahí es de OTRO paciente (otra identidad),
  // no se lo pisa: se archiva aparte, marcado como fila eliminada, y el
  // borrador nuevo queda limpio. Antes el texto de X sin confirmar se perdía
  // en cuanto el médico escribía en Y (D-4), o Y heredaba la marca de X.
  const idNuevo = meta && typeof meta === "object" ? meta.rowId ?? null : null;
  const idPrevio = prevMeta?.rowId ?? null;
  if (
    vigente &&
    mismaPestana &&
    textoPrevio.trim() &&
    idNuevo != null &&
    idPrevio != null &&
    String(idNuevo) !== String(idPrevio)
  ) {
    archivarHuerfano(ls, key, prev, idPrevio);
    return meta;
  }
  if (!prevMeta?.rowGone || !vigente || !mismaPestana) return meta;
  // Mismo paciente (o sin identidad): la marca es pegajosa. Si el guardado
  // nuevo no trae `tabId`, se queda el del borrador marcado: sin él,
  // `recoverUnsavedDrafts` lo ofrecería en cualquier pestaña con ese título.
  const nextMeta =
    meta && typeof meta === "object" && meta.rowGone
      ? meta
      : {
          ...(meta && typeof meta === "object" ? meta : {}),
          tabId: tabIdNuevo ?? tabIdPrevio,
          ...((idNuevo ?? idPrevio) != null ? { rowId: idNuevo ?? idPrevio } : {}),
          rowGone: true,
        };
  // Y un huérfano nunca se pisa con algo que no lo contiene (el dictado o los
  // exámenes que llegan tarde traen solo su tramo, no la evolución entera): el
  // huérfano anterior se archiva y los dos se ofrecen al recuperar.
  if (textoPrevio.trim() && !String(value ?? "").startsWith(textoPrevio)) {
    archivarHuerfano(ls, key, prev, idPrevio ?? idNuevo);
  }
  return nextMeta;
};

// True si `meta` es de un huérfano (`rowGone` con identidad) y la clave ya
// tiene un borrador vigente, NO marcado, de OTRA identidad en la misma pestaña.
const huerfanoContraBorradorVivoAjeno = (ls, key, meta) => {
  if (!meta || typeof meta !== "object" || !meta.rowGone || meta.rowId == null) return false;
  let prev = null;
  try {
    prev = JSON.parse(ls.getItem(key) || "null");
  } catch {
    return false;
  }
  const prevMeta = prev && typeof prev.meta === "object" ? prev.meta : null;
  if (!prevMeta || prevMeta.rowGone || prevMeta.rowId == null) return false;
  if (String(prevMeta.rowId) === String(meta.rowId)) return false;
  if (!prev.ts || Date.now() - Number(prev.ts) > MAX_AGE_MS) return false;
  if (typeof prev.value !== "string" || !prev.value.trim()) return false;
  const a = meta.tabId ?? null;
  const b = prevMeta.tabId ?? null;
  return a == null || b == null || String(a) === String(b);
};

// Mueve el borrador `prev` (de la identidad `rowId`) a una clave propia con
// número de fila negativo, marcado `rowGone`. Mismo dueño, misma pestaña,
// misma columna: `recoverUnsavedDrafts` lo ofrece como "fila eliminada" y "Usar
// lo mío" lo guarda como paciente extra. Busca un lugar libre (hasta
// ARCHIVO_SLOTS por identidad y columna); si el mismo texto ya está archivado,
// no lo duplica. Si no se puede escribir (cuota), no se hace nada: el borrador
// nuevo lo pisará, igual que antes de este arreglo.
const ARCHIVO_SLOTS = 50;
const archivarHuerfano = (ls, key, prev, rowId) => {
  const parsed = parseKey(key);
  if (!parsed) return;
  const n = Number(rowId);
  const base = ARCHIVO_OFFSET + (Number.isFinite(n) ? Math.abs(n) : Date.now() % ARCHIVO_OFFSET) * ARCHIVO_SLOTS;
  const claveArchivo = (k) =>
    `${PREFIX}${encodeURIComponent(parsed.owner)}:${encodeURIComponent(parsed.tab)}:${-(base + k)}:${encodeURIComponent(parsed.column)}`;
  let destino = claveArchivo(ARCHIVO_SLOTS - 1);
  for (let k = 0; k < ARCHIVO_SLOTS; k += 1) {
    const candidata = claveArchivo(k);
    let ocupada = null;
    try {
      ocupada = JSON.parse(ls.getItem(candidata) || "null");
    } catch {
      ocupada = null;
    }
    if (!ocupada) {
      destino = candidata;
      break;
    }
    if (ocupada.value === prev.value) return; // ya archivado
    // Continuación del mismo huérfano (el médico o el dictado siguieron
    // escribiendo): el texto nuevo contiene al archivado, se lo reemplaza en
    // su lugar en vez de gastar otro.
    if (
      typeof ocupada.value === "string" &&
      ocupada.value &&
      String(prev.value ?? "").startsWith(ocupada.value)
    ) {
      destino = candidata;
      break;
    }
  }
  try {
    ls.setItem(
      destino,
      JSON.stringify({
        value: prev.value,
        ts: Number(prev.ts) || Date.now(),
        meta: { ...(prev.meta || {}), rowGone: true, filaOriginal: parsed.row },
      }),
    );
  } catch {
    /* sin lugar: se comporta como antes (el nuevo pisa al viejo) */
  }
};

/**
 * #1449 H-E: marca como `rowGone` los borradores vigentes de (pestaña, fila)
 * que son de la identidad `rowId` (o que no traen identidad: borradores de
 * antes de este arreglo). Lo llama Turno.js apenas sabe que esa fila se
 * eliminó en otro aparato, para que la recuperación los ofrezca como "fila
 * eliminada" aunque otro paciente ya ocupe ese número. No toca borradores de
 * otra identidad ni de otra pestaña.
 */
export const marcarBorradoresFilaEliminada = (tab, row, { rowId = null, tabId = null } = {}) => {
  const ls = storage();
  if (!ls || !tab || !row) return;
  const owner = ownerToken();
  allEntries(ls).forEach((e) => {
    if (e.corrupt || e.owner !== owner || e.tab !== String(tab) || e.row !== row) return;
    const meta = e.meta || {};
    if (meta.rowGone) return;
    if (rowId != null && meta.rowId != null && String(meta.rowId) !== String(rowId)) return;
    if (tabId != null && meta.tabId != null && String(meta.tabId) !== String(tabId)) return;
    try {
      ls.setItem(
        e.key,
        JSON.stringify({
          value: e.value,
          ts: e.ts || Date.now(),
          meta: {
            ...meta,
            ...(rowId != null && meta.rowId == null ? { rowId } : {}),
            rowGone: true,
          },
        }),
      );
    } catch {
      /* nada que hacer */
    }
  });
};

/**
 * #1449 H-E: los borradores de una pestaña que hay que ofrecer YA como "fila
 * eliminada" (el aviso de Turno.js tiene "Recuperar mi texto"): los marcados
 * `rowGone` y los de una identidad que el servidor dio por eliminada. Mismo
 * shape que los conflictos de `partitionRecoverableDrafts` (`rowMissing:
 * true`), para que "Usar lo mío" los guarde como paciente extra. Puro.
 */
export const conflictosDeFilasEliminadas = (drafts, { tabId = null, eliminadas = null } = {}) =>
  (drafts || [])
    .filter((d) => {
      const meta = d?.meta || {};
      if (tabId != null && meta.tabId != null && String(meta.tabId) !== String(tabId)) return false;
      if (meta.rowGone) return true;
      return Boolean(eliminadas && meta.rowId != null && eliminadas.has(String(meta.rowId)));
    })
    .map((d) => ({ ...d, cama: "", rowMissing: true }));

/** Borra el respaldo: el servidor ya confirmó ese valor. */
export const clearDraft = (tab, row, column) => {
  const ls = storage();
  if (!ls || !tab || !row || !column) return;
  try {
    ls.removeItem(makeKey(tab, row, column));
  } catch {
    /* nada que hacer */
  }
};

/**
 * Borradores sin confirmar de una pestaña, ya filtrados por antigüedad Y por
 * dueño: un borrador escrito con OTRO `authToken` no se devuelve nunca, así
 * quien esté logueado ahora no puede ni leer (mucho menos reponer) texto que
 * dejó otra sesión en este mismo navegador.
 * Devuelve [{ row, column, value, ts, meta }] ordenados del más viejo al más
 * nuevo.
 */
export const listDrafts = (tab) => {
  const ls = storage();
  if (!ls || !tab) return [];
  const now = Date.now();
  const wanted = String(tab);
  const owner = ownerToken();
  const out = [];
  allEntries(ls).forEach((e) => {
    if (e.corrupt || !e.ts || now - e.ts > MAX_AGE_MS) {
      try {
        ls.removeItem(e.key);
      } catch {
        /* nada que hacer */
      }
      return;
    }
    if (e.owner !== owner) return;
    if (e.tab !== wanted) return;
    out.push({ row: e.row, column: e.column, value: e.value, ts: e.ts, meta: e.meta });
  });
  out.sort((a, b) => a.ts - b.ts);
  return out;
};

/**
 * Borra TODO el buffer, de cualquier pestaña y cualquier dueño. Se llama
 * desde `logout()` (helpers/authentication-helpers.js), al lado de
 * `purgarDictado()`: en un computador de turno el siguiente que entra es otra
 * persona, y el aislamiento por `authToken` (arriba) ya lo protege desde el
 * primer keystroke — esto es la segunda capa, para que ni siquiera quede
 * dando vueltas en el disco. Ley 21.719, mismo criterio que `dictado-buffer.js`.
 */
export const purgarDrafts = () => {
  const ls = storage();
  if (!ls) return;
  const keys = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    if (key && key.startsWith(PREFIX)) keys.push(key);
  }
  keys.forEach((key) => {
    try {
      ls.removeItem(key);
    } catch {
      /* nada que hacer */
    }
  });
};

/**
 * Mueve las claves del buffer de un `authToken` viejo a uno nuevo. SOLO se
 * llama cuando la rotación es de la MISMA persona (`establecerSesion(token,
 * {mismoUsuario: true})`: "Cambiar clave", "Cerrar las otras sesiones", o que
 * un admin revoque el token). Sin esto el aislamiento por dueño (arriba)
 * vuelve huérfano e invisible el propio borrador del médico apenas rota su
 * token (#1255, 4ª pasada del revisor, probes P2a/P4).
 *
 * No reabre el cruce entre médicos: solo mueve claves cuyo owner sea
 * EXACTAMENTE `oldOwner`, nunca toca las de otro dueño. Si el navegador no
 * deja escribir la clave nueva, la vieja queda donde estaba — no se pierde
 * nada, sigue siendo recuperable por su dueño real la próxima vez.
 */
export const rekeyDrafts = (oldOwner, newOwner) => {
  const ls = storage();
  if (!ls || !oldOwner || !newOwner || oldOwner === newOwner) return;
  const movidas = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    const parsed = parseKey(key);
    if (!parsed || parsed.owner !== oldOwner) continue;
    movidas.push({ key, parsed });
  }
  movidas.forEach(({ key, parsed }) => {
    const payload = ls.getItem(key);
    if (payload == null) return;
    const newKey = `${PREFIX}${encodeURIComponent(newOwner)}:${encodeURIComponent(
      parsed.tab,
    )}:${parsed.row}:${encodeURIComponent(parsed.column)}`;
    try {
      ls.setItem(newKey, payload);
      ls.removeItem(key);
    } catch {
      /* sin lugar para la nueva: la vieja se queda donde estaba */
    }
  });
};

/**
 * Purga claves del buffer que `parseKey` no puede interpretar de forma
 * segura — en la práctica, el formato VIEJO de 3 partes
 * (`turno.draft.v1:<tab>:<row>:<col>`, sin dueño) que escribía la versión
 * desplegada hasta el 2026-09-10, más cualquier otra corrupción bajo este
 * mismo prefijo. Adoptarlas reabriría el cruce de PHI entre médicos que el
 * aislamiento por dueño vino a cerrar (no tienen owner que filtrar), y
 * dejarlas quietas las vuelve inmortales: ni `allEntries` ni `evict` las
 * tocan, así que ni siquiera entran al chequeo de 24 h (#1255, 4ª pasada del
 * revisor, probe P5a). Se corre una vez al cargar este módulo (ver el pie del
 * archivo) — es idempotente y no hace nada si no hay ninguna.
 * Nunca toca claves de otro prefijo (el diario del dictado usa el suyo).
 */
export const purgarClavesViejas = () => {
  const ls = storage();
  if (!ls) return;
  const keys = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    if (key && key.startsWith(PREFIX) && !parseKey(key)) keys.push(key);
  }
  keys.forEach((key) => {
    try {
      ls.removeItem(key);
    } catch {
      /* nada que hacer */
    }
  });
};

/**
 * Decide qué hacer con un borrador sin confirmar frente a lo que hoy tiene el
 * servidor. Conservador a propósito — nunca descarta texto del médico:
 *
 *   "none"    → el servidor ya tiene exactamente eso (el PATCH sí había llegado,
 *               solo se perdió la confirmación). Se borra el borrador.
 *   "restore" → el borrador es una continuación de lo que hay en el servidor
 *               (o el servidor está vacío): reponerlo no pierde nada, va solo.
 *   "ask"     → divergieron de verdad (otro dispositivo escribió algo distinto).
 *               No se toca nada: se le ofrece al médico.
 */
export const classifyDraft = (draftValue, serverValue) => {
  const draft = String(draftValue ?? "");
  const server = String(serverValue ?? "");
  if (draft === server) return "none";
  if (!draft.trim()) return "none"; // un borrador vacío nunca pisa contenido
  // El servidor ya contiene todo lo del borrador y más (caso típico: quedó
  // guardado un parcial del streaming del LLM). Nada que recuperar.
  if (server.startsWith(draft)) return "none";
  if (!server.trim()) return "restore";
  if (draft.startsWith(server)) return "restore";
  return "ask";
};

/**
 * Reparte los borradores sin confirmar de una pestaña contra las filas que
 * trajo el servidor, para `recoverUnsavedDrafts` (Turno.js). Puro y sin
 * efectos: no toca localStorage ni la red, así se puede probar sin montar
 * el componente entero.
 *
 * ANTES (revisor final #1449, H2, 2026-09-26): una fila que desapareció
 * (censo con `DELETE_FIXED`, "Eliminar paciente") mientras el médico
 * escribía se trataba IGUAL que "la columna no aplica a esta pestaña": las
 * dos se descartaban en silencio (`clearDraft`), sin avisarle a nadie. Si el
 * PATCH de esa edición nunca había llegado a confirmarse (la fila ya no
 * existía: 404 `row_not_found`, ver `update_row` en `turno_pipeline.py`), el
 * texto se perdía sin dejar rastro.
 *
 * AHORA: una fila ausente (`!row`) es SIEMPRE un conflicto
 * (`rowMissing: true`), nunca un descarte automático: se ofrece en el
 * diálogo de recuperación igual que una divergencia real. Solo se descarta
 * en silencio cuando la fila SÍ existe pero la columna no aplica a esta
 * pestaña, o cuando `classify` dice "none" (el servidor ya tiene ese valor).
 *
 * ⚠️ CORRECCIÓN (revisor final vuelta 2, H8, #1449): "la fila ya no existe"
 * NO se puede decidir solo mirando si HOY hay una fila con ese número. El
 * backend reusa `sheet_row_number` (`append_row` en turno_pipeline.py hace
 * max+1), así que un paciente NUEVO puede nacer exactamente en el número que
 * dejó libre el que se borró. Sin esta marca, `byRow.get(draft.row)`
 * encontraba la fila del paciente nuevo, `classify` la comparaba contra un
 * PREVIO vacío y daba "restore": el texto del paciente borrado se PATCHeaba
 * solo, en silencio, sobre la ficha del paciente nuevo. Por eso `saveCell`
 * (Turno.js) vuelve a guardar el borrador con `meta.rowGone: true` apenas
 * confirma el 404 `row_not_found`, y acá esa marca fuerza el conflicto
 * SIEMPRE, sin mirar si el número de fila está ocupado hoy.
 *
 * `opts.classify` (default: `classifyDraft` de este mismo módulo) decide
 * "none"/"restore"/"ask" para una fila que SÍ existe. `opts.getCama(row)`
 * identifica la cama de la fila para el mensaje del diálogo. Turno.js tiene
 * su propio `normalizeKey` y no hace falta duplicarlo acá.
 */
export const partitionRecoverableDrafts = (drafts, serverRows, opts = {}) => {
  const classify = typeof opts.classify === "function" ? opts.classify : classifyDraft;
  const getCama = typeof opts.getCama === "function" ? opts.getCama : () => "";
  const byRow = new Map((serverRows || []).map((r) => [r._row, r]));
  const toRestore = [];
  const conflicts = [];
  const toClear = [];
  (drafts || []).forEach((draft) => {
    // H8: `rowGone` es más fuerte que cualquier fila que exista hoy con ese
    // número (pudo nacer un paciente nuevo ahí, ver comentario arriba).
    // #1449 H-E / D-6: y un borrador con identidad (`meta.rowId`) que no es la
    // de la fila que HOY ocupa ese número tampoco es de esa fila, aunque nunca
    // haya recibido un 404 (el PATCH se cayó por red y otro paciente heredó el
    // número): se ofrece como fila eliminada, nunca se repone solo.
    const enServidor = draft.meta?.rowGone ? null : byRow.get(draft.row);
    const idDraft = draft.meta?.rowId ?? null;
    const idServidor = enServidor?._id ?? null;
    const otraIdentidad =
      enServidor && idDraft != null && idServidor != null && String(idDraft) !== String(idServidor);
    const row = otraIdentidad ? null : enServidor;
    if (!row) {
      // La fila ya no existe (o está marcada `rowGone`): NUNCA se descarta
      // en silencio (H2), ni se lo asigna al paciente que reusó el número.
      conflicts.push({ ...draft, cama: "", rowMissing: true });
      return;
    }
    if (!(draft.column in row)) {
      toClear.push(draft);
      return;
    }
    const verdict = classify(draft.value, row[draft.column]);
    if (verdict === "none") {
      toClear.push(draft);
      return;
    }
    // `rowId`: la identidad contra la que se comparó. El PATCH de "restore" /
    // "Usar lo mío" la manda como `row_id`: si entre esta lectura y el PATCH
    // el número cambia de dueño, el servidor lo rechaza con 409.
    const entry = {
      ...draft,
      cama: getCama(row),
      rowMissing: false,
      ...(row._id != null ? { rowId: row._id } : {}),
    };
    if (verdict === "restore") toRestore.push(entry);
    else conflicts.push(entry);
  });
  return { toRestore, conflicts, toClear };
};

/**
 * Arma el request para persistir un borrador recuperado (`recoverUnsavedDrafts`
 * y `applyDraftConflicts` en Turno.js). NUNCA hace la llamada: solo decide
 * método/URL/payload, para poder probarlo sin mockear `client` ni la red.
 *
 * Una fila viva (`rowMissing` falsy) se PATCHea como siempre. Una fila que ya
 * no existe (`rowMissing: true`) NUNCA se vuelve a PATCHear: el backend
 * (#1449, revisor F2) no la recrea: sería el mismo 404 `row_not_found` para
 * siempre, con la falsa promesa de "se reintenta solo". En su lugar se manda
 * como paciente EXTRA nuevo (`POST turno/rows/`, `is_extra: true`): el texto
 * del médico no depende de que esa fila borrada vuelva a existir.
 *
 * El caller todavía tiene que envolver el `payload` con `withUnidad(...)`
 * (unidad/tab activos) antes de mandarlo, acá no se conoce esa parte del
 * estado de Turno.js.
 */
export const buildDraftPersistRequest = (tabName, draft) => {
  if (draft?.rowMissing) {
    return {
      method: "post",
      url: "/api/medical_analysis/turno/rows/",
      payload: {
        tab: tabName,
        is_extra: true,
        values: { [draft.column]: draft.value },
      },
    };
  }
  const rowId = draft?.rowId ?? draft?.meta?.rowId ?? null;
  return {
    method: "patch",
    url: "/api/medical_analysis/turno/rows/",
    payload: {
      tab: tabName,
      row: draft.row,
      // #1449 H-E: la identidad viaja; sin ella (borrador viejo y backend
      // viejo) el PATCH es el de siempre.
      ...(rowId != null ? { row_id: rowId } : {}),
      values: { [draft.column]: draft.value },
    },
  };
};

/**
 * #1449 H-E: persiste un borrador recuperado. `send(req)` hace la llamada real
 * (Turno.js le suma `withUnidad`). El PATCH de una fila viva lleva `row_id`:
 * entre el GET que la clasificó y este PATCH el número puede haber cambiado de
 * paciente, y ahí el servidor responde 409 `row_mismatch` (o 404
 * `row_not_found` si ya no hay fila) sin escribir nada.
 *
 *   - `comoExtra: false` (reposición automática al cargar): ante ese rechazo
 *     NO se hace nada más; `filaEliminada: true` le dice al caller que lo
 *     marque y lo ofrezca como conflicto "fila eliminada".
 *   - `comoExtra: true` ("Usar lo mío", decisión explícita del médico): ante
 *     ese rechazo el texto se guarda como paciente EXTRA nuevo, igual que un
 *     conflicto `rowMissing`. Nunca se pega al que heredó el número, y nunca se
 *     pierde.
 *
 * Devuelve `{ ok, via: "patch" | "extra" | null, response, filaEliminada, error }`.
 * Nunca lanza.
 */
export const persistirBorrador = async (tabName, draft, send, { comoExtra = false } = {}) => {
  const req = buildDraftPersistRequest(tabName, draft);
  try {
    const response = await send(req);
    return { ok: true, via: req.method === "post" ? "extra" : "patch", response, filaEliminada: false };
  } catch (error) {
    const filaEliminada = req.method === "patch" && esFilaEliminada(error);
    if (!filaEliminada || !comoExtra) {
      return { ok: false, via: null, response: null, filaEliminada, error };
    }
    try {
      const response = await send(buildDraftPersistRequest(tabName, { ...draft, rowMissing: true }));
      return { ok: true, via: "extra", response, filaEliminada: true };
    } catch (error2) {
      return { ok: false, via: null, response: null, filaEliminada: true, error: error2 };
    }
  }
};

// Se corre una sola vez al cargar el módulo, es decir al arranque de la app
// (o de cada test file que lo importe): así las claves de 3 partes que dejó
// la versión desplegada hasta hoy no quedan invisibles e inmortales en el
// disco del médico esperando un logout que puede no llegar nunca (el
// computador se apaga, no se cierra sesión a mano). try/catch por si corre en
// un entorno sin `localStorage` (SSR) — el arranque nunca se bloquea por esto.
try {
  purgarClavesViejas();
} catch {
  /* nada que hacer */
}

export const __testing = { makeKey, parseKey, PREFIX, MAX_AGE_MS, ownerToken, ARCHIVO_OFFSET };
